import { logger } from '../services/logger.ts';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';

// Define notification event types and descriptions
const EVENT_TYPES = [
  {
    id: 'BET_PLACED',
    name: 'Trade Placed',
    description: 'Receive notifications when you place a trade'
  },
  {
    id: 'LMSR_SHARES_SOLD',
    name: 'LMSR sale completed',
    description: 'Receive notifications when you sell shares on share-priced markets'
  },
  {
    id: 'MARKET_RESOLVED',
    name: 'Market Resolution',
    description: 'Receive notifications when markets you trade on are resolved'
  },
  {
    id: 'MARKET_ENDING_SOON',
    name: 'Market Ending Soon',
    description: 'Receive notifications when markets you trade on are ending soon'
  },
  {
    id: 'DEPOSIT_SUCCESSFUL',
    name: 'Deposit Successful',
    description: 'Receive notifications when your deposits are successful'
  },
  {
    id: 'DEPOSIT_FAILED',
    name: 'Deposit Failed',
    description: 'Receive notifications when your deposits fail'
  },
  {
    id: 'WITHDRAWAL_SUCCESSFUL',
    name: 'Withdrawal Successful',
    description: 'Receive notifications when your withdrawals are successful'
  },
  {
    id: 'WITHDRAWAL_FAILED',
    name: 'Withdrawal Failed',
    description: 'Receive notifications when your withdrawals fail'
  }
];

// Define interface for user preferences
interface NotificationPreference {
  user_id: string;
  event_type: string;
  email_enabled: boolean;
  push_enabled: boolean;
}

export function UserNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<Record<string, NotificationPreference>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }
        
        setLoading(true);
        
        // Fetch existing preferences
        const { data, error } = await supabase
          .from('user_notification_preferences')
          .select('*')
          .eq('user_id', user.id);
          
        if (error) {
          throw error;
        }
        
        // Convert array to record object with event_type as key
        const prefsRecord: Record<string, NotificationPreference> = {};
        if (data) {
          data.forEach((pref: NotificationPreference) => {
            prefsRecord[pref.event_type] = pref;
          });
        }
        
        // Initialize missing preferences with defaults (all enabled)
        EVENT_TYPES.forEach(eventType => {
          if (!prefsRecord[eventType.id]) {
            prefsRecord[eventType.id] = {
              user_id: user.id,
              event_type: eventType.id,
              email_enabled: true,
              push_enabled: true
            };
          }
        });
        
        setPreferences(prefsRecord);
      } catch (error) {
        logger.error('Error fetching notification preferences:', error);
        setError('Failed to load your notification preferences');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPreferences();
  }, [user, navigate]);
  
  const handleToggle = (eventType: string, channel: 'email_enabled' | 'push_enabled') => {
    setPreferences(prev => {
      const updated = { ...prev };
      if (updated[eventType]) {
        updated[eventType] = {
          ...updated[eventType],
          [channel]: !updated[eventType][channel]
        };
      }
      return updated;
    });
  };
  
  const savePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      // Convert preferences object to array for Supabase
      const prefsArray = Object.values(preferences);
      
      // Upsert preferences
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert(prefsArray);
        
      if (error) {
        throw error;
      }
      
      setSuccessMessage('Notification preferences saved successfully');
    } catch (error) {
      logger.error('Error saving notification preferences:', error);
      setError('Failed to save your notification preferences');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Notification Settings</h1>
      
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <p className="mb-4 text-gray-600">
          Customize how you receive notifications from Wayja. You can choose which events trigger notifications
          and through which channels you receive them.
        </p>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {successMessage}
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="py-3 px-4 text-left">Event</th>
                <th className="py-3 px-4 text-center">Email</th>
                <th className="py-3 px-4 text-center">Push</th>
              </tr>
            </thead>
            <tbody>
              {EVENT_TYPES.map((eventType) => (
                <tr key={eventType.id} className="border-b">
                  <td className="py-3 px-4">
                    <div className="font-medium">{eventType.name}</div>
                    <div className="text-sm text-gray-500">{eventType.description}</div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={preferences[eventType.id]?.email_enabled ?? true}
                        onChange={() => handleToggle(eventType.id, 'email_enabled')}
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-wayja-teal"></div>
                    </label>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={preferences[eventType.id]?.push_enabled ?? true}
                        onChange={() => handleToggle(eventType.id, 'push_enabled')}
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-wayja-teal"></div>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-wayja-teal hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wayja-teal"
          >
            {saving ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 